import React, { useRef, useEffect, useState, useCallback } from 'react';
import Tippy from '@tippyjs/react/headless';

export interface FilterTabOption<T = string | number> {
  label: React.ReactNode;
  value: T;
}

export interface FilterTabsProps<T = string | number> {
  /** 前置标签，例如 “专长：” */
  label?: React.ReactNode;
  /** 选项列表 */
  options: FilterTabOption<T>[];
  /** 当前选中的值（单值或多选数组） */
  value: T | T[];
  /** 切换选中的回调 */
  onChange: (value: T) => void;
  /** 是否显示“全部”选项，默认为 true */
  showAll?: boolean;
  /** “全部”选项的文本，默认为 "全部专长" */
  allLabel?: React.ReactNode;
  /** “全部”选项对应的 value，默认为 "" */
  allValue?: T;
  /** 对齐方式，可选 'start' | 'center' | 'end'，默认为 'start' */
  align?: 'start' | 'center' | 'end';
  /** 自定义外层容器 class */
  className?: string;
  /** 选项按钮的基础 class */
  itemClassName?: string;
  /** 选中状态下的 class */
  activeItemClassName?: string;
  /** 未选中状态下的 class */
  inactiveItemClassName?: string;
  /** 当宽度不足溢出时是否显示气泡选择按钮，默认为 true */
  showPopoverOnOverflow?: boolean;
  /** 气泡弹窗标题，默认为 "全部分类" */
  popoverTitle?: React.ReactNode;
}

export default function FilterTabs<T extends string | number = string>({
  label,
  options,
  value,
  onChange,
  showAll = true,
  allLabel = '全部专长',
  allValue = '' as T,
  align = 'start',
  className = '',
  itemClassName = '',
  activeItemClassName = '',
  inactiveItemClassName = '',
  showPopoverOnOverflow = true,
  popoverTitle = '全部分类',
}: FilterTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // 检查滚动状态并显示/隐藏两侧模糊渐变阴影及溢出按钮
  const checkScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setIsOverflowing(scrollWidth > clientWidth + 2);
    setShowLeftShadow(scrollLeft > 4);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 4);
  }, []);

  // 监听鼠标滚轮与滚动位置更新
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    checkScrollState();

    const handleWheel = (e: WheelEvent) => {
      if (container.scrollWidth > container.clientWidth) {
        if (e.deltaY !== 0) {
          e.preventDefault();
          container.scrollBy({
            left: e.deltaY * 1.2,
            behavior: 'smooth',
          });
        }
      }
    };

    const handleScroll = () => {
      checkScrollState();
    };

    const handleResize = () => {
      checkScrollState();
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [checkScrollState, options.length]);

  const defaultItemBase =
    'text-sm px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer shrink-0 select-none';
  const defaultActive =
    'bg-gradient-to-r from-primary-500 to-primary-300 text-white font-medium shadow-md shadow-primary-500/20 scale-[1.02]';
  const defaultInactive =
    'border border-background-300 bg-white text-foreground-500 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/60 hover:shadow-sm';

  const baseCls = itemClassName || defaultItemBase;
  const activeCls = activeItemClassName || defaultActive;
  const inactiveCls = inactiveItemClassName || defaultInactive;

  const allOptionsList: FilterTabOption<T>[] = showAll
    ? [{ label: allLabel, value: allValue }, ...options]
    : options;

  const checkIsActive = (optValue: T): boolean => {
    if (Array.isArray(value)) {
      if (optValue === allValue) {
        return value.length === 0;
      }
      return value.includes(optValue);
    }
    return value === optValue;
  };

  const handleSelectOption = (optValue: T) => {
    onChange(optValue);
    setPopoverOpen(false);
    // 自动将选中的 Tab 滚动到可视区域内
    if (containerRef.current) {
      const targetBtn = containerRef.current.querySelector<HTMLElement>(
        `[data-tab-value="${encodeURIComponent(String(optValue))}"]`
      );
      if (targetBtn) {
        targetBtn.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  };

  return (
    <div className={`flex items-center flex-1 min-w-0 ${className}`}>
      {label && (
        <span className="text-sm text-foreground-400 mr-2.5 whitespace-nowrap shrink-0">
          {label}
        </span>
      )}

      {/* 滚动容器包装层，用来承载两侧阴影遮罩 */}
      <div className="relative flex-1 min-w-0 flex items-center">
        {/* 左侧淡入渐变阴影遮罩 */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-r from-white via-white/80 to-transparent z-10 transition-opacity duration-300 ${showLeftShadow ? 'opacity-100' : 'opacity-0'
            }`}
        />

        {/* 滚动容器 */}
        <div
          ref={containerRef}
          className={`flex items-center ${align === 'end' ? 'justify-end' : 'justify-start'
            } overflow-x-auto flex-nowrap flex-1 py-1.5 px-0.5 no-scrollbar scroll-smooth min-w-0`}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div className={`flex items-center gap-2.5 ${align === 'center' ? 'm-auto' : ''}`}>
            {allOptionsList.map((opt) => {
              const isActive = checkIsActive(opt.value);
              return (
                <button
                  key={String(opt.value)}
                  data-tab-value={encodeURIComponent(String(opt.value))}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                  }}
                  className={`${baseCls} ${isActive ? activeCls : inactiveCls}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 右侧淡出渐变阴影遮罩 */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l from-white via-white/80 to-transparent z-10 transition-opacity duration-300 ${showRightShadow ? 'opacity-100' : 'opacity-0'
            }`}
        />
      </div>

      {/* 宽度不足溢出时展示的气泡选择按钮 */}
      {showPopoverOnOverflow && isOverflowing && (
        <div className="shrink-0 ml-1.5 relative">
          <Tippy
            interactive={true}
            visible={popoverOpen}
            onClickOutside={() => setPopoverOpen(false)}
            placement="bottom-end"
            offset={[0, 8]}
            appendTo={() => document.body}
            render={(attrs) => (
              <div
                {...attrs}
                className="w-72 sm:w-80 p-3.5 rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl shadow-xl z-50 animate-in fade-in zoom-in-95 duration-150 outline-none"
              >
                <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-gray-100">
                  <span className="text-xs md:text-sm font-bold text-gray-800 flex items-center gap-1.5">
                    <i className="ri-apps-2-line text-[#10b981]"></i>
                    <span>{popoverTitle}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setPopoverOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto pr-1">
                  {allOptionsList.map((opt) => {
                    const isActive = checkIsActive(opt.value);
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => handleSelectOption(opt.value)}
                        className={`${baseCls} ${isActive ? activeCls : inactiveCls}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          >
            <button
              type="button"
              onClick={() => setPopoverOpen(!popoverOpen)}
              title="选择分类"
              className={`w-8 h-8 md:w-9 md:h-9 aspect-square rounded-full border transition-all flex items-center justify-center shrink-0 cursor-pointer select-none ${popoverOpen
                  ? 'bg-[#10b981] text-white border-[#10b981] shadow-2xs'
                  : 'bg-gray-100/90 border-transparent text-gray-700 hover:bg-gray-200/80 hover:text-gray-900'
                }`}
            >
              <i className="ri-apps-2-line text-sm md:text-base"></i>
            </button>
          </Tippy>
        </div>
      )}
    </div>
  );
}

